/* eslint-disable @typescript-eslint/no-explicit-any */
import { action, observable, computed, reaction, autorun, set } from 'mobx';

export type IFormErrors<T> = { [k in keyof T]: string };
export type IFormTouched<T> = { [k in keyof T]: boolean };
export type ValidationSchema = any | (() => any);

export interface IConfigFormStore<T> {
    formValidator: IFormValidator;
    initialValues: T;
    validationObject?: IValidationObject;
}

export interface IFormValidator {
    getValidationSchema(validationObject: IValidationObject): {};
}

export interface IValidationObject {
    [key: string]: {};
}

export class FormStore<IFormValues> {
    @observable public formValidator: IFormValidator;
    @observable public validationObject: IValidationObject = {};
    @observable public values: IFormValues;
    @observable public errors: IFormErrors<IFormValues> = {} as IFormErrors<IFormValues>;
    @observable public touched: IFormTouched<IFormValues> = {} as IFormTouched<IFormValues>;
    private initialValues: IFormValues;

    constructor(config: IConfigFormStore<IFormValues>) {
        this.initialValues = config.initialValues;
        this.values = config.initialValues;
        this.formValidator = config.formValidator;
        autorun(() => {
            if (this.validationObject) {
                this.runValidationSchema();
            }
        });
        reaction(() => this.encodedValues, this.runValidationSchema);
    }

    @computed
    get validationSchema() {
        return this.formValidator.getValidationSchema(this.validationObject);
    }

    @action
    public setValues = (values: IFormValues) => {
        set(this.values, values);
    };

    @action
    public setErrors = (errors: IFormErrors<IFormValues>) => {
        this.errors = errors;
    };

    @action
    public setFieldValidations = (name: string, validations: {}) => {
        this.validationObject = { ...this.validationObject, [name]: validations };
    };

    @action
    public removeValidationField = (name: string) => {
        if (this.validationObject[name]) {
            delete this.validationObject[name];
        }
    };

    @action
    public clearStore = () => {
        this.errors = {} as IFormErrors<IFormValues>;
        this.touched = {} as IFormTouched<IFormValues>;
        this.validationObject = {};
        this.values = this.initialValues;
    };

    @computed
    public get encodedValues() {
        return JSON.stringify(this.values);
    }

    @action
    public runValidationSchema = () => {
        const validationSchema = this.validationSchema;
        const values = this.values;
        // eslint-disable-next-line no-use-before-define
        validateYupSchema<IFormValues>(values, validationSchema).then(
            () => {
                this.setErrors({} as IFormErrors<IFormValues>);
            },
            (err: any) => {
                // eslint-disable-next-line no-use-before-define
                this.setErrors(yupToFormErrors(err));
            }
        );
    };

    @action
    public setFieldValue = <K extends keyof IFormValues>(name: K, value: IFormValues[K]) => {
        this.values[name] = value;
    };

    @action
    public setTouchedField = <K extends keyof IFormValues>(name: K) => {
        // eslint-disable-next-line prefer-object-spread
        this.touched = Object.assign({}, this.touched, {
            [name]: true
        });
    };
    @action
    public blurField = <K extends keyof IFormValues>(name: K) => {
        this.setTouchedField(name);
        this.runValidationSchema();
    };

    public getError = <K extends Extract<keyof IFormValues, string>>(name: K) => {
        return this.touched[name] && this.errors[name];
    };

    @computed
    get isValid() {
        return this.errors && Object.keys(this.errors).length === 0;
    }
}

export function yupToFormErrors(yupError: any) {
    const reducer = (accumulator: any, currentValue: any) => {
        if (accumulator[currentValue.path]) {
            return accumulator;
        }
        const errorMessage = typeof currentValue.message === 'string' ? currentValue.message : '';

        return { ...accumulator, [currentValue.path]: errorMessage };
    };

    return yupError.inner.reduce(reducer, {});
}

export function validateYupSchema<T>(
    values: any,
    schema: any,
    sync: boolean = false,
    context: any = {}
): Promise<Partial<T>> {
    const validateData: any = {};
    Object.keys(values)
        .filter(k => values.hasOwnProperty(k))
        .forEach(
            (key: string) => (validateData[key] = values[key] !== '' ? values[key] : undefined)
        );

    return schema[sync ? 'validateSync' : 'validate'](validateData, {
        abortEarly: false,
        context: context
    });
}
